import * as prismic from "@prismicio/client";
import * as prismicNext from "@prismicio/next";
import config from "../slicemachine.config.json";

/**
 * The project's Prismic repository name.
 */
export const repositoryName =
  process.env.NEXT_PUBLIC_PRISMIC_ENVIRONMENT || config.repositoryName;

/**
 * A list of Route Resolver objects that define how a document's `url` field is resolved.
 *
 * {@link https://prismic.io/docs/route-resolver#route-resolver}
 */
// TODO: Update the routes array to match your project's route structure.
const routes: prismic.ClientConfig["routes"] = [
  // Examples:
  {
    type: "landing_page",
    path: "/",
  },
  {
    type: "blog_landing_page",
    path: "/resources",
  },
  {
    type: "blog_post",
    path: "/resources/:uid",
  },
  {
    type: "features_page",
    path: "/features",
  },
  {
    type: "pricing_page",
    path: "/pricing",
  },
];

/**
 * A `fetch` implementation that retries transient failures (network errors,
 * timeouts, and 5xx/429 responses) with a short exponential backoff.
 *
 * Prismic's CDN occasionally returns a transient error or drops a connection.
 * Without retries a single hiccup makes the awaiting Server Component throw,
 * which bubbles up to `app/error.tsx` ("Something went wrong") for the whole
 * page — the user then has to reload to recover. Retrying here absorbs those
 * blips server-side so the error boundary is never reached. Successful (and
 * Next.js-cached) responses return on the first attempt, unchanged.
 */
const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504]);

const retryingFetch: typeof fetch = async (input, init) => {
  const maxAttempts = 3;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(input, init);
      if (RETRYABLE_STATUS.has(response.status) && attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 250));
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 250));
        continue;
      }
    }
  }

  throw lastError;
};

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 *
 * @param config - Configuration for the Prismic client.
 */
export const createClient = (config: prismicNext.CreateClientConfig = {}) => {
  const client = prismic.createClient(repositoryName, {
    routes,
    fetch: retryingFetch,
    fetchOptions:
      process.env.NODE_ENV === "production"
        ? { next: { tags: ["prismic"] }, cache: "force-cache" }
        : { next: { revalidate: 5 } },
    ...config,
  });

  prismicNext.enableAutoPreviews({
    client,
  });

  return client;
};
